import { useEffect, useState } from "react";
import {
  Drawer,
  Loader,
  ScrollArea,
  Skeleton,
  UnstyledButton,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft, IconCheck, IconCopy } from "@tabler/icons-react";
import type {
  AgentPromptVersionDetail,
  AgentPromptVersionSummary,
} from "@superagents/types/api";
import Button from "@/design-system/components/Button";
import { feedbackDateLabel } from "@/features/ai-feedback/utils/aiFeedbackAuthor";
import { useLoadMoreOnScroll } from "@/shared/hooks/useLoadMoreOnScroll";
import { useMobileScreen } from "@/shared/hooks/useMobileScreen";
import { getRequestErrorMessage } from "@/shared/utils/getRequestErrorMessage";
import {
  useAgentPromptVersion,
  useAgentPromptVersions,
} from "../../hooks/useAgentPromptVersions";
import { describeLineDiff, summarizeLineDiff } from "../../utils/promptDiff";
import { PromptDiff } from "../PromptDiff";
import classes from "./PromptVersionsDrawer.module.css";

interface PromptVersionsDrawerProps {
  opened: boolean;
  onClose: () => void;
}

// Sem autor: a v1 semeada com o prompt de antes do histórico, ou uma versão
// gravada fora da tela (seed, script). Só a v1 ganha o rótulo.
const authorLabel = (version: AgentPromptVersionSummary) =>
  version.author?.name?.trim() ||
  (version.versionNumber === 1 ? "Versão inicial" : null);

const versionMeta = (version: AgentPromptVersionSummary) => {
  const author = authorLabel(version);
  return [
    feedbackDateLabel(version.createdAt),
    ...(author ? [author] : []),
  ].join(" · ");
};

/**
 * Histórico de versões do prompt do cliente (SA-1486), só leitura: a lista à
 * esquerda e o que mudou na versão escolhida contra a anterior à direita. No
 * celular, a lista e o detalhe se alternam. Voltar a uma versão antiga é
 * trabalho da Área de Treinamento: o dev ajusta o rascunho e publica.
 */
export default function PromptVersionsDrawer({
  opened,
  onClose,
}: PromptVersionsDrawerProps) {
  const { isMobileScreen } = useMobileScreen();
  const versions = useAgentPromptVersions(opened);
  const list = versions.data ?? [];
  const [chosenId, setChosenId] = useState<string | null>(null);
  // No celular o detalhe só abre depois de tocar numa versão.
  const [showDetail, setShowDetail] = useState(false);
  const selected =
    list.find((version) => version.documentId === chosenId) ?? list[0] ?? null;
  const detail = useAgentPromptVersion(
    opened ? (selected?.documentId ?? null) : null
  );
  const { sentinelRef, supported } = useLoadMoreOnScroll({
    hasNextPage: Boolean(versions.hasNextPage),
    isFetchingNextPage: versions.isFetchingNextPage,
    fetchNextPage: versions.fetchNextPage,
    paused: Boolean(versions.isFetchNextPageError),
  });
  // Pedido do Léo (29/10): o texto inteiro de qualquer versão, para reaproveitar.
  const clipboard = useClipboard({ timeout: 2000 });

  // Reabrir começa da lista e da versão em uso, não da última vista.
  useEffect(() => {
    if (!opened) {
      setChosenId(null);
      setShowDetail(false);
      clipboard.reset();
    }
    // `clipboard` muda a cada render: só a abertura importa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Sem permissão ou fora de contexto seguro, a cópia falha em silêncio.
  useEffect(() => {
    if (clipboard.error) {
      notifications.show({
        message:
          "Não foi possível copiar o prompt. Selecione o texto e copie à mão.",
        color: "red",
        autoClose: 5000,
      });
    }
  }, [clipboard.error]);

  const choose = (id: string) => {
    // "Copiado" é da versão copiada: não passa para a próxima.
    clipboard.reset();
    setChosenId(id);
    setShowDetail(true);
  };

  const showList = !isMobileScreen || !showDetail;
  const showSelected = selected && (!isMobileScreen || showDetail);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      // Drawer não tem `fullScreen`: no celular ocupa a largura toda.
      size={isMobileScreen ? "100%" : "xl"}
      closeButtonProps={{ "aria-label": "Fechar o histórico" }}
      title={<span className={classes.title}>Histórico de versões</span>}>
      {versions.isLoading ? (
        <div
          data-testid="versions-skeleton"
          className={classes.listColumn}>
          {[0, 1, 2, 3].map((key) => (
            <Skeleton
              key={key}
              height={64}
              radius="md"
            />
          ))}
        </div>
      ) : versions.isError ? (
        <div
          className={classes.state}
          role="alert">
          <p className={classes.stateText}>
            {getRequestErrorMessage(
              versions.error,
              "Não foi possível carregar as versões."
            )}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void versions.refetch()}>
            Tentar de novo
          </Button>
        </div>
      ) : list.length === 0 ? (
        <p className={classes.stateText}>
          Ainda não há versões. Elas começam a aparecer a partir do próximo
          salvamento das instruções.
        </p>
      ) : (
        <div className={classes.layout}>
          {showList && (
            <ScrollArea
              className={classes.listColumn}
              type="auto">
              <ul className={classes.list}>
                {list.map((version, index) => (
                  <li key={version.documentId}>
                    <UnstyledButton
                      className={classes.item}
                      data-selected={
                        (!isMobileScreen && version === selected) || undefined
                      }
                      aria-current={
                        (!isMobileScreen && version === selected) || undefined
                      }
                      onClick={() => choose(version.documentId)}>
                      <span className={classes.itemTop}>
                        <span
                          className={classes.tag}
                          data-current={index === 0 || undefined}>
                          v{version.versionNumber}
                        </span>
                        {index === 0 && (
                          <span className={classes.current}>Em uso</span>
                        )}
                      </span>
                      <span
                        className={classes.note}
                        data-empty={!version.note?.trim() || undefined}>
                        {version.note?.trim() || "Sem descrição"}
                      </span>
                      <span className={classes.meta}>
                        {versionMeta(version)}
                      </span>
                      <span className={classes.modelRow}>
                        <span
                          className={classes.model}
                          data-changed={version.modelChanged || undefined}>
                          {version.model}
                        </span>
                        {version.modelChanged && (
                          <span className={classes.modelChanged}>
                            Modelo trocado
                          </span>
                        )}
                      </span>
                    </UnstyledButton>
                  </li>
                ))}
              </ul>
              {versions.hasNextPage && (
                <div
                  ref={sentinelRef}
                  className={classes.sentinel}
                  aria-hidden
                />
              )}
              {(versions.hasNextPage || versions.total > list.length) && (
                <div className={classes.more}>
                  {versions.isFetchingNextPage && (
                    <Loader
                      size="sm"
                      aria-label="Carregando versões anteriores"
                    />
                  )}
                  <span className={classes.meta}>
                    Mostrando {list.length} de {versions.total}
                  </span>
                  {/* Rolar carrega sozinho; o botão fica para erro ou navegador sem suporte. */}
                  {versions.hasNextPage &&
                    (!supported || versions.isFetchNextPageError) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={versions.isFetchingNextPage}
                        onClick={() => void versions.fetchNextPage()}>
                        {versions.isFetchNextPageError
                          ? "Tentar de novo"
                          : "Carregar versões anteriores"}
                      </Button>
                    )}
                </div>
              )}
            </ScrollArea>
          )}
          {showSelected && (
            <section className={classes.detail}>
              {isMobileScreen && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={() => setShowDetail(false)}>
                  Voltar
                </Button>
              )}
              <div className={classes.detailHeader}>
                <h3 className={classes.detailTitle}>
                  O que mudou na v{selected.versionNumber}
                </h3>
                <Button
                  variant="outline"
                  size="xs"
                  disabled={!detail.data}
                  leftSection={
                    clipboard.copied ? (
                      <IconCheck size={14} />
                    ) : (
                      <IconCopy size={14} />
                    )
                  }
                  onClick={() =>
                    detail.data && clipboard.copy(detail.data.prompt)
                  }>
                  {clipboard.copied
                    ? "Copiado"
                    : `Copiar prompt da v${selected.versionNumber}`}
                </Button>
              </div>
              {selected.note?.trim() && (
                <p className={classes.detailNote}>{selected.note}</p>
              )}
              <VersionChanges
                detail={detail.data}
                isLoading={detail.isLoading}
                isError={detail.isError}
                error={detail.error}
                onRetry={() => void detail.refetch()}
              />
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
}

function VersionChanges({
  detail,
  isLoading,
  isError,
  error,
  onRetry,
}: {
  detail: AgentPromptVersionDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  if (isLoading || (!detail && !isError)) {
    return (
      <Skeleton
        height={240}
        radius="md"
      />
    );
  }
  if (isError || !detail) {
    return (
      <div
        className={classes.state}
        role="alert">
        <p className={classes.stateText}>
          {getRequestErrorMessage(error, "Não foi possível carregar a versão.")}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRetry}>
          Tentar de novo
        </Button>
      </div>
    );
  }
  if (detail.previousPrompt === null) {
    return (
      <ScrollArea
        className={classes.diff}
        type="auto">
        <pre className={classes.pre}>{detail.prompt}</pre>
      </ScrollArea>
    );
  }
  const textChanged = detail.previousPrompt !== detail.prompt;
  const modelChanged = detail.previousModel !== detail.model;
  return (
    <>
      {modelChanged && (
        <p className={classes.summary}>
          Modelo trocado de{" "}
          <span className={classes.model}>{detail.previousModel}</span> para{" "}
          <span
            className={classes.model}
            data-changed>
            {detail.model}
          </span>
        </p>
      )}
      {textChanged && (
        <>
          <p className={classes.summary}>
            {describeLineDiff(
              summarizeLineDiff(detail.previousPrompt, detail.prompt)
            )}
          </p>
          <ScrollArea
            className={classes.diff}
            type="auto">
            <PromptDiff
              before={detail.previousPrompt}
              after={detail.prompt}
            />
          </ScrollArea>
        </>
      )}
      {!textChanged && !modelChanged && (
        <p className={classes.summary}>Texto igual ao da versão anterior.</p>
      )}
    </>
  );
}
